import {
  CropType,
  MoistureLevel,
  Season,
  SimpleSoilTexture,
  useCropTypesLazyQuery,
} from "generated/hooks";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { titleCase } from "~/util/string.util";
import { useFetchAll } from "~/hooks/useFetchAll";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Checkbox } from "~/components/ui/checkbox";

export interface EditCharacteristicsDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (
    moisture: MoistureLevel,
    season: Season,
    texture: SimpleSoilTexture,
    cropTypeId: string,
    isReferenceZone: boolean
  ) => void;
  defaultMoisture: MoistureLevel;
  defaultSeason: Season;
  defaultTexture: SimpleSoilTexture;
  defaultCropType: Partial<CropType>;
  defaultIsReferenceZone: boolean;
  error?: string;
}

export const EditCharacteristicsDialog = ({
  isOpen,
  onCancel,
  onConfirm,
  defaultMoisture,
  defaultSeason,
  defaultTexture,
  defaultCropType,
  defaultIsReferenceZone,
  error,
}: EditCharacteristicsDialogProps) => {
  const [fetchCropTypes] = useCropTypesLazyQuery();
  const { data: allCropTypes } = useFetchAll(fetchCropTypes, (d) => d.cropTypes, {
    variables: { after: null },
  });
  const [moisture, setMoisture] = useState(defaultMoisture);
  const [season, setSeason] = useState(defaultSeason);
  const [texture, setTexture] = useState(defaultTexture);
  const [cropType, setCropType] = useState<string>(defaultCropType.id ?? "");
  const [isReferenceZone, setIsReferenceZone] = useState(defaultIsReferenceZone);

  const cropTypeOptions = useMemo(
    () => allCropTypes,
    [allCropTypes]
  );

  useEffect(() => {
    if (isOpen) {
      setMoisture(defaultMoisture);
      setSeason(defaultSeason);
      setTexture(defaultTexture);
      setCropType(defaultCropType.id ?? "");
      setIsReferenceZone(defaultIsReferenceZone);
    }
  }, [
    defaultCropType.id,
    defaultIsReferenceZone,
    defaultMoisture,
    defaultSeason,
    defaultTexture,
    isOpen,
  ]);

  useEffect(() => {
    if (!cropType && cropTypeOptions.length > 0) {
      setCropType(cropTypeOptions[0].id);
    }
  }, [cropType, cropTypeOptions]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!cropType) {
      return;
    }
    onConfirm(moisture, season, texture, cropType, isReferenceZone);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <DialogContent>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit characteristics</DialogTitle>
            <DialogDescription>
              Update the soil characteristics for this test.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="pes-test-moisture">Moisture</Label>
              <Select
                value={moisture}
                onValueChange={(value) => setMoisture(value as MoistureLevel)}
              >
                <SelectTrigger id="pes-test-moisture" className="w-full">
                  <SelectValue placeholder="Select moisture level" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(MoistureLevel).map((value) => (
                    <SelectItem key={value} value={value}>
                      {titleCase(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pes-test-season">Season</Label>
              <Select
                value={season}
                onValueChange={(value) => setSeason(value as Season)}
              >
                <SelectTrigger id="pes-test-season" className="w-full">
                  <SelectValue placeholder="Select season" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(Season).map((value) => (
                    <SelectItem key={value} value={value}>
                      {titleCase(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pes-test-texture">Texture</Label>
              <Select
                value={texture}
                onValueChange={(value) =>
                  setTexture(value as SimpleSoilTexture)
                }
              >
                <SelectTrigger id="pes-test-texture" className="w-full">
                  <SelectValue placeholder="Select soil texture" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(SimpleSoilTexture).map((value) => (
                    <SelectItem key={value} value={value}>
                      {titleCase(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pes-test-crop-type">Crop type</Label>
              <Select
                value={cropType}
                onValueChange={(value) => setCropType(value)}
                disabled={cropTypeOptions.length === 0}
              >
                <SelectTrigger id="pes-test-crop-type" className="w-full">
                  <SelectValue
                    placeholder={
                      cropTypeOptions.length === 0
                        ? "No crop types available"
                        : "Select crop type"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {cropTypeOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start gap-2 sm:col-span-2">
              <Checkbox
                id="pes-test-is-reference-zone"
                checked={isReferenceZone}
                onCheckedChange={(checked) => {
                  setIsReferenceZone(checked === true);
                }}
              />
              <Label htmlFor="pes-test-is-reference-zone">Is Reference?</Label>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Changing any value will trigger reprocessing of the test, which
              can take a few minutes.
            </p>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={!cropType}>
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
